import type { ReactNode } from 'react';
import { Info, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';

export function InfoBox({ children }: { children: ReactNode }) {
  return (
    <div className="my-4 flex gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-500/30 dark:bg-blue-500/10">
      <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600 dark:text-blue-400" />
      <div className="text-sm text-blue-800 dark:text-blue-200">{children}</div>
    </div>
  );
}

export function WarningBox({ children }: { children: ReactNode }) {
  return (
    <div className="my-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
      <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="text-sm text-amber-800 dark:text-amber-200">{children}</div>
    </div>
  );
}

export function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <h3 className="mb-2 mt-6 text-base font-semibold text-slate-800 dark:text-slate-100">
      {children}
    </h3>
  );
}

export function Paragraph({ children }: { children: ReactNode }) {
  return (
    <p className="mb-3 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
      {children}
    </p>
  );
}

export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-forest-700 dark:bg-slate-800 dark:text-forest-300">
      {children}
    </code>
  );
}

export function DocTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="my-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
            {headers.map((h) => (
              <th
                key={h}
                className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={cn(
                'border-b border-slate-100 last:border-b-0 dark:border-slate-800',
                i % 2 === 1 && 'bg-slate-50/50 dark:bg-slate-800/50',
              )}
            >
              {row.map((cell, j) => (
                <td
                  key={j}
                  className="px-4 py-2 text-slate-700 dark:text-slate-300"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Bullet list. Accepts either an `items` array (legacy form used
 * extensively in docs-content) or `children` (newer "What's new"
 * section that authors `<li>` directly for richer markup). Either
 * way the styling stays consistent.
 */
export function BulletList({
  items,
  children,
}: {
  items?: ReactNode[];
  children?: ReactNode;
}) {
  if (items) {
    return (
      <ul className="mb-3 space-y-1.5 pl-5 marker:text-slate-400 dark:marker:text-slate-500">
        {items.map((item, i) => (
          <li
            key={i}
            className="list-disc text-sm leading-relaxed text-slate-700 dark:text-slate-300"
          >
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <ul className="mb-3 space-y-1.5 pl-5 marker:text-slate-400 [&>li]:list-disc [&>li]:text-sm [&>li]:leading-relaxed [&>li]:text-slate-700 dark:[&>li]:text-slate-300 dark:marker:text-slate-500">
      {children}
    </ul>
  );
}

export function NumberedList({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mb-3 space-y-1.5 pl-5 marker:text-slate-400 dark:marker:text-slate-500">
      {items.map((item, i) => (
        <li
          key={i}
          className="list-decimal text-sm leading-relaxed text-slate-700 dark:text-slate-300"
        >
          {item}
        </li>
      ))}
    </ol>
  );
}
